import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';

export interface ILMSModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE_CLASSES: Record<NonNullable<ILMSModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function ILMSModal({ open, onClose, title, children, footer, size = 'md' }: ILMSModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Drag the window by its title bar (on request) to see the page behind;
  // double-click the title bar to put it back in the middle.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const moved = offset.x !== 0 || offset.y !== 0;

  useEffect(() => { if (open) setOffset({ x: 0, y: 0 }); }, [open]);

  const onPointerDown = (e: React.PointerEvent) => {
    // Mouse / pen on a wider screen only; the close button keeps its click.
    if (e.pointerType === 'touch' || window.innerWidth < 640 || (e.target as HTMLElement).closest('button')) return;
    drag.current = { px: e.clientX, py: e.clientY, x: offset.x, y: offset.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const box = dialogRef.current?.getBoundingClientRect();
    if (!d || !box) return;
    // Keep the title bar on screen so it can always be grabbed again.
    const maxX = window.innerWidth / 2 + box.width / 2 - 80;
    const maxY = window.innerHeight / 2 + box.height / 2 - 60;
    const x = Math.max(-maxX, Math.min(maxX, d.x + e.clientX - d.px));
    const y = Math.max(-(window.innerHeight / 2 - box.height / 2) - 8, Math.min(maxY, d.y + e.clientY - d.py));
    setOffset({ x, y });
  };
  const onPointerUp = () => { drag.current = null; };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Once moved aside, the page behind shows through more and a click on
          it doesn't close the window (nothing typed is lost). */}
      <div className={cn('absolute inset-0 transition-colors', moved ? 'bg-ink-900/15' : 'bg-ink-900/50')} onClick={moved ? undefined : onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={moved ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
        className={cn(
          'relative bg-white w-full sm:rounded-lg shadow-popover flex flex-col',
          'max-h-[92vh] sm:max-h-[85vh] rounded-t-lg',
          SIZE_CLASSES[size]
        )}
      >
        {title && (
          <div
            className="flex items-center justify-between gap-3 border-b border-ink-300/15 px-4 sm:px-5 py-3.5 shrink-0 sm:cursor-move select-none"
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            onDoubleClick={() => setOffset({ x: 0, y: 0 })}
            title="Drag to move · double-click to centre"
          >
            <h2 className="text-section-title">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-surface-muted text-ink-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="overflow-y-auto px-4 sm:px-5 py-4 flex-1">{children}</div>
        {footer && <div className="border-t border-ink-300/15 px-4 sm:px-5 py-3.5 flex items-center justify-end gap-2 shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
